import { useEffect, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { inventoryStore } from '@/stores/inventory.store';
import { modalStore } from '@/stores/modal.store';
import { coinflipStore, coinflipIntent } from '@/stores/coinflip.store';
import CoinflipInventoryPicker from '@/components/Modals/Inventory/CoinflipInventoryPicker';
import CoinflipJoinFooter from '@/components/Modals/Inventory/CoinflipJoinFooter';
import Chip from '@/components/Coinflip/Chip';

const CreateSteamCoinflip = () => {
    const {
        steamItems,
        selectedItemIds,
        maxSelectedItems,
        setSelectedById,
        clearInventory,
        loadSteamInventory,
        uncacheSteamInventory,
    } = inventoryStore((state) => state);
    const { setTitle, setComponent, setData } = modalStore((state) => state);
    const { minbet } = coinflipStore((state) => state);

    const [loading, setLoading] = useState(true);
    const [side, setSide] = useState<1 | 2>(1);

    async function load(force = false) {
        const {
            data: {
                response: {
                    global: { appid },
                },
            },
        } = await ApiRequest.get('/settings/list');
        if (force) await uncacheSteamInventory(appid, 2);
        await loadSteamInventory(appid, 2);
        setLoading(false);
    }

    useEffect(() => {
        load();
        return () => clearInventory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const pickerItems = steamItems.map((it) => ({
        id: BigInt(it.assetId),
        name: it.name,
        priceUsd: it.priceUsd,
        colorHex: it.colorHex,
        icon: it.icon,
        iconBackground: it.iconBackground,
    }));

    const inventoryTotal = pickerItems.reduce((s, i) => s + i.priceUsd, 0);
    const selected = pickerItems.filter((i) => selectedItemIds.has(i.id));
    const selectedAmount = selected.reduce((s, i) => s + i.priceUsd, 0);
    const canCreate = selected.length > 0 && selectedAmount >= minbet;

    async function create() {
        if (!canCreate) return;
        const assetIds = Array.from(selectedItemIds).map((i) => i.toString());
        const {
            data: {
                success,
                response: { offerId },
            },
        } = await ApiRequest.post('/coinflip/create/steam', { assetIds, side });
        if (success) {
            // Initiator marker: when the game's coinflipCreated broadcast lands
            // (after the trade is accepted), only this tab auto-opens it.
            coinflipIntent.create = true;
            setComponent('Steam trade');
            setTitle('CONFIRM TRADE');
            setData({ tradeId: offerId });
        }
    }

    return (
        <>
            <div className="flex items-center justify-center gap-3 mb-4 pt-2">
                <span className="text-[11px] uppercase tracking-[0.1em] text-text_3 font-semibold">
                    Create as
                </span>
                <Chip
                    variant="red"
                    selected={side === 1}
                    onClick={() => setSide(1)}
                    aria-label="Side 1 — red chip"
                />
                <Chip
                    variant="black"
                    selected={side === 2}
                    onClick={() => setSide(2)}
                    aria-label="Side 2 — black chip"
                />
            </div>

            <CoinflipInventoryPicker
                items={pickerItems}
                selectedItemIds={selectedItemIds}
                setSelectedById={setSelectedById}
                maxItems={maxSelectedItems}
                loading={loading}
                onRefresh={() => {
                    setLoading(true);
                    clearInventory();
                    load(true);
                }}
                footer={
                    <CoinflipJoinFooter
                        inventoryTotal={inventoryTotal}
                        selectedCount={selected.length}
                        selectedAmount={selectedAmount}
                        minBet={minbet}
                        onAction={create}
                        actionLabel="CREATE"
                        actionDisabled={!canCreate}
                    />
                }
            />
        </>
    );
};

export default CreateSteamCoinflip;
